import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { pdfjs } from 'react-pdf';
// import 'react-pdf/dist/Page/AnnotationLayer.css';
// import 'react-pdf/dist/Page/TextLayer.css';
import { useDialog } from 'context/DialogContext';
import './CrossSectionLinePopup.css';
import { AccountType } from 'models';
import { createPdfFromImage, downloadBlob, formatDirection, loadImageAsBase64, loadImageDimensions, retrieveDirectionValue } from '../helper';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { DIRECTION_OPTIONS } from 'utils/constanst';
import { downloadFileFromS3 } from 'utils/function';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const CrossSectionLinePopup = ({ isOpen, onClose, sensor, accountType }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const { success, error: showError } = useDialog();
    const { id: cameraId, data } = sensor;
    const { typeCamera, camera1Direction, camera2Direction } = data || {};

    useEffect(() => {
        if (isOpen && cameraId) {
            loadImage();
        }
    }, [isOpen, cameraId]);

    const loadImage = async () => {
        setLoading(true);
        setError(null);
        try {
            const imagePath = `counted_camera_imgs/${cameraId}`;
            const result = await getUrl({
                    key: imagePath,
                    options: { validateObjectExistence: true }
                });
            setImageUrl(result.url.toString());
        } catch (err) {
            console.error('Error loading image:', err);
            const statusCode = err.metadata?.httpStatusCode;
            const message = statusCode === 404 ? '' : '画像の読み込みに失敗しました';
            setError(message);
            setImageUrl(null);
        } finally {
            setLoading(false);
        }
    };

    const handleReference = () => {
        fileInputRef.current?.click();
    };

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !cameraId) return;

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            showError('画像ファイルのみアップロードできます（JPG, JPEG、PNG、GIF, PDF）');
            resetFileInput();
            return;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            showError('ファイルサイズは10MB以下にしてください');
            resetFileInput();
            return;
        }

        setLoading(true);
        setError(null);

        let fileToUpload = file;

        try {
            // Convert PDF to image
            if (file.type.includes('application/pdf')) {
                const arrayBuffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);

                const loadingTask = pdfjs.getDocument({ data: uint8Array });
                const pdf = await loadingTask.promise;
                const page = await pdf.getPage(1);

                const scale = 1.4;
                const viewport = page.getViewport({ scale });

                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({
                    canvasContext: context,
                    viewport: viewport,
                }).promise;

                // Convert canvas to blob with JPEG compression for smaller file size
                const imageBlob = await new Promise((resolve) => {
                    canvas.toBlob(resolve, 'image/jpeg', 1.0);
                });
                fileToUpload = new File([imageBlob], `${cameraId}.jpg`, { type: 'image/jpeg' });
            }

            await uploadData({
                key: `counted_camera_imgs/${cameraId}`,
                data: fileToUpload,
            }).result;
            await loadImage();
            success('画像のアップロードに成功しました');
        } catch (err) {
            setError('アップロードに失敗しました');
            showError('アップロードに失敗しました');
        } finally {
            setLoading(false);
            resetFileInput();
        }
    };

    const getFilename = () => {
        if (typeCamera === 'TWIN') {
            return `${sensor.name}_1${formatDirection(camera1Direction)}_2${formatDirection(camera2Direction)}_断面ライン.pdf`;
        }

        const directionValue = retrieveDirectionValue(sensor);
        return `${sensor.name}_${directionValue}_断面ライン.pdf`;
    };

    const handleDownload = async () => {
        if (!imageUrl) return;
        try {
            setIsDownloading(true);
            const blob = await downloadFileFromS3(`counted_camera_imgs/${cameraId}`, 'blob')
            const filename = getFilename();
            const objectUrl = URL.createObjectURL(blob);
            const { width, height } = await loadImageDimensions(objectUrl);
            const base64data = await loadImageAsBase64(blob);
            const pdf = await createPdfFromImage(base64data, width, height);
            const pdfBlob = pdf.output('blob');
            URL.revokeObjectURL(objectUrl);
            downloadBlob(pdfBlob, filename);
        } catch (err) {
            console.log('Error downloading file:', err);
            if (err.metadata?.httpStatusCode === 404) {
                showError('ダウンロードするファイルが存在しません');
                return;
            }
            showError('ダウンロードに失敗しました');
        } finally {
            setIsDownloading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="cross-section-popup-overlay" onClick={onClose}>
            {isDownloading && <FanLoadingIcon />}
            <div className="cross-section-popup" onClick={(e) => e.stopPropagation()}>
                <div className="cross-section-popup-header">
                    <button className="cross-section-popup-close" onClick={onClose}>
                        X
                    </button>
                    <div className="cross-section-popup-actions">
                        {accountType === AccountType.ADMIN && (<>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*, application/pdf"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <button
                                className="cross-section-popup-button"
                                onClick={handleReference}
                                disabled={loading}
                            >
                                参照
                            </button>
                        </>)
                        }
                        <button
                            className="cross-section-popup-button"
                            onClick={handleDownload}
                            disabled={!imageUrl || loading}
                        >
                            ダウンロード
                        </button>
                    </div>
                </div>
                <div className="cross-section-popup-body">
                    {loading && <div className="cross-section-popup-loading">読み込み中...</div>}
                    {error && <div className="cross-section-popup-error">{error}</div>}
                    {!loading && !error && imageUrl && (
                        <img src={imageUrl} alt="断面ライン" className="cross-section-popup-image" />
                    )}
                    {!loading && !error && !imageUrl && (
                        <div className="cross-section-popup-no-image">アップロードされている画像がありません</div>
                    )}
                </div>
            </div>
        </div>
    );
};

CrossSectionLinePopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    cameraId: PropTypes.string
};

export default CrossSectionLinePopup;
